import { useState, useEffect, ReactNode } from 'react'
import { X, Trash2, Download, Upload } from 'lucide-react'
import api from '@/lib/api'

const CCYS = ['USD', 'EUR', 'GBP', 'CHF']
const STAGES = ['pre_seed', 'seed', 'series_a', 'series_b', 'series_c', 'growth', 'other']
const INSTRUMENTS = ['common', 'preferred', 'safe', 'convertible']
const DIST_TYPES = ['partial_exit', 'full_exit', 'distribution_only', 'dividend', 'secondary']

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className={`mt-10 w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} rounded-lg bg-white shadow-xl`}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  )
}

const label = 'block text-xs font-medium text-gray-600 mb-1'
const input = 'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none'
const btn = 'rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
const btnGhost = 'rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'

function Field({ label: l, children }: { label: string; children: ReactNode }) {
  return <div><label className={label}>{l}</label>{children}</div>
}

// --------------------------------------------------------------- Investment
export function InvestmentModal({ portfolioId, existing, onClose, onSaved }:
  { portfolioId: string; existing?: any; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<any>(existing || {
    startup_name: '', consolidation_company: '', invested_at: '', denomination_ccy: 'USD',
    stage: 'seed', vehicle: 'direct', lead: '', sector: '', country: '', is_follow_on: false,
    cost_instrument_type: 'common',
    cost_shares: '', cost_share_price: '', cost_amount: '', cost_valuation_cap: '',
    cur_shares: '', cur_share_price: '', cur_valuation_cap: '', cur_amount_override: '',
    entry_ownership_pct: '', cur_ownership_pct: '', entry_company_valuation: '', cur_company_valuation: '',
    notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const isSafe = ['safe', 'convertible'].includes(f.cost_instrument_type)
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value })
  const num = (v: any) => v === '' || v == null ? null : Number(v)

  const submit = async () => {
    setSaving(true); setErr('')
    const body = {
      startup_name: f.startup_name, consolidation_company: f.consolidation_company || null,
      invested_at: f.invested_at, denomination_ccy: f.denomination_ccy, stage: f.stage,
      vehicle: f.vehicle, lead: f.lead || null, cost_instrument_type: f.cost_instrument_type,
      cost_shares: num(f.cost_shares), cost_share_price: num(f.cost_share_price),
      cost_amount: num(f.cost_amount), cost_valuation_cap: num(f.cost_valuation_cap),
      cur_instrument_type: f.cost_instrument_type, cur_shares: num(f.cur_shares),
      cur_share_price: num(f.cur_share_price), cur_valuation_cap: num(f.cur_valuation_cap),
      cur_amount_override: num(f.cur_amount_override), notes: f.notes || null,
      sector: f.sector || null, country: f.country || null, is_follow_on: !!f.is_follow_on,
      entry_ownership_pct: num(f.entry_ownership_pct), cur_ownership_pct: num(f.cur_ownership_pct),
      entry_company_valuation: num(f.entry_company_valuation), cur_company_valuation: num(f.cur_company_valuation),
    }
    try {
      if (existing?.id) await api.put(`/private/investments/${existing.id}`, body)
      else await api.post(`/private/portfolios/${portfolioId}/investments`, body)
      onSaved(); onClose()
    } catch (e: any) { setErr(e?.response?.data?.detail || 'Save failed') }
    finally { setSaving(false) }
  }

  return (
    <Modal title={existing ? 'Edit investment' : 'Add investment'} onClose={onClose} wide>
      <div className="space-y-4">
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Investment basics</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Startup name"><input className={input} value={f.startup_name} onChange={set('startup_name')} /></Field>
            <Field label="Consolidation company (optional)"><input className={input} value={f.consolidation_company} onChange={set('consolidation_company')} placeholder="groups multiple entries" /></Field>
            <Field label="Date of investment"><input type="date" className={input} value={f.invested_at} onChange={set('invested_at')} /></Field>
            <Field label="Denomination currency"><select className={input} value={f.denomination_ccy} onChange={set('denomination_ccy')}>{CCYS.map(c => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Stage"><select className={input} value={f.stage} onChange={set('stage')}>{STAGES.map(s => <option key={s} value={s}>{s}</option>)}</select></Field>
            <Field label="Vehicle"><input className={input} value={f.vehicle} onChange={set('vehicle')} placeholder="direct / syndicate / SPV" /></Field>
            <Field label="Lead"><input className={input} value={f.lead} onChange={set('lead')} /></Field>
            <Field label="Sector / industry"><input className={input} value={f.sector} onChange={set('sector')} placeholder="Fintech, BioTech…" /></Field>
            <Field label="Country"><input className={input} value={f.country} onChange={set('country')} /></Field>
            <label className="flex items-center gap-2 pt-5 text-sm text-gray-700"><input type="checkbox" checked={!!f.is_follow_on} onChange={e => setF({ ...f, is_follow_on: e.target.checked })} />Follow-on investment</label>
          </div>
        </div>
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Instrument</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Instrument type"><select className={input} value={f.cost_instrument_type} onChange={set('cost_instrument_type')}>{INSTRUMENTS.map(i => <option key={i} value={i}>{i}</option>)}</select></Field>
            <div />
            {isSafe ? <>
              <Field label="Invested amount"><input className={input} type="number" value={f.cost_amount} onChange={set('cost_amount')} /></Field>
              <div />
              <Field label="Entry valuation cap"><input className={input} type="number" value={f.cost_valuation_cap} onChange={set('cost_valuation_cap')} /></Field>
              <Field label="Current ('now') cap"><input className={input} type="number" value={f.cur_valuation_cap} onChange={set('cur_valuation_cap')} /></Field>
            </> : <>
              <Field label="Cost: number of shares"><input className={input} type="number" value={f.cost_shares} onChange={set('cost_shares')} /></Field>
              <Field label="Cost: share price"><input className={input} type="number" value={f.cost_share_price} onChange={set('cost_share_price')} /></Field>
              <Field label="Current: number of shares"><input className={input} type="number" value={f.cur_shares} onChange={set('cur_shares')} /></Field>
              <Field label="Current: share price"><input className={input} type="number" value={f.cur_share_price} onChange={set('cur_share_price')} /></Field>
            </>}
            <Field label="Manual residual override (optional)"><input className={input} type="number" value={f.cur_amount_override} onChange={set('cur_amount_override')} /></Field>
          </div>
        </div>
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Ownership & valuation</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Entry ownership %"><input className={input} type="number" step="0.01" value={f.entry_ownership_pct} onChange={set('entry_ownership_pct')} /></Field>
            <Field label="Current ownership %"><input className={input} type="number" step="0.01" value={f.cur_ownership_pct} onChange={set('cur_ownership_pct')} /></Field>
            <Field label="Entry company valuation"><input className={input} type="number" value={f.entry_company_valuation} onChange={set('entry_company_valuation')} /></Field>
            <Field label="Current company valuation"><input className={input} type="number" value={f.cur_company_valuation} onChange={set('cur_company_valuation')} /></Field>
          </div>
        </div>
        {err && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btn} disabled={saving || !f.startup_name || !f.invested_at} onClick={submit}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  )
}

// --------------------------------------------------------------- Distribution
export function DistributionModal({ positions, onClose, onSaved }:
  { positions: any[]; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<any>({ investment_id: positions[0]?.id || '', event_date: '', type: 'partial_exit', denomination_ccy: 'USD', cash_proceeds: '', residual_after_event: '' })
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('')
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value })
  const submit = async () => {
    setSaving(true); setErr('')
    try {
      await api.post('/private/distributions', {
        investment_id: f.investment_id, event_date: f.event_date, type: f.type,
        denomination_ccy: f.denomination_ccy, cash_proceeds: Number(f.cash_proceeds || 0),
        residual_after_event: f.residual_after_event === '' ? null : Number(f.residual_after_event),
      })
      onSaved(); onClose()
    } catch (e: any) { setErr(e?.response?.data?.detail || 'Save failed') }
    finally { setSaving(false) }
  }
  return (
    <Modal title="Record distribution" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Startup / position"><select className={input} value={f.investment_id} onChange={set('investment_id')}>{positions.map(p => <option key={p.id} value={p.id}>{p.startup_name} ({p.denomination_ccy})</option>)}</select></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Event date"><input type="date" className={input} value={f.event_date} onChange={set('event_date')} /></Field>
          <Field label="Type"><select className={input} value={f.type} onChange={set('type')}>{DIST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
          <Field label="Denomination"><select className={input} value={f.denomination_ccy} onChange={set('denomination_ccy')}>{CCYS.map(c => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Cash proceeds"><input type="number" className={input} value={f.cash_proceeds} onChange={set('cash_proceeds')} /></Field>
          <Field label="Residual after event (optional)"><input type="number" className={input} value={f.residual_after_event} onChange={set('residual_after_event')} /></Field>
        </div>
        {err && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btn} disabled={saving || !f.investment_id || !f.event_date} onClick={submit}>{saving ? 'Saving…' : 'Save distribution'}</button>
        </div>
      </div>
    </Modal>
  )
}

// --------------------------------------------------------------- FX editor
export function FxModal({ portfolioId, current, onClose, onSaved }:
  { portfolioId: string; current: Record<string, number>; onClose: () => void; onSaved: () => void }) {
  const [rates, setRates] = useState<Record<string, string>>({
    USD: '1', EUR: String(current?.EUR ?? 0.92), GBP: String(current?.GBP ?? 0.79), CHF: String(current?.CHF ?? 0.88),
  })
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10))
  const [saving, setSaving] = useState(false)
  const submit = async () => {
    setSaving(true)
    try {
      await api.post(`/private/portfolios/${portfolioId}/fx`, {
        as_of: asOf, is_active: true,
        rates: { USD: 1, EUR: Number(rates.EUR), GBP: Number(rates.GBP), CHF: Number(rates.CHF) },
      })
      onSaved(); onClose()
    } finally { setSaving(false) }
  }
  return (
    <Modal title="Spot FX snapshot" onClose={onClose}>
      <p className="mb-3 text-xs text-gray-500">Target currency units per 1 USD. Residual values are converted with this snapshot.</p>
      <Field label="Valuation date"><input type="date" className={input} value={asOf} onChange={e => setAsOf(e.target.value)} /></Field>
      <div className="mt-3 grid grid-cols-4 gap-3">
        {['USD', 'EUR', 'GBP', 'CHF'].map(c => (
          <Field key={c} label={`1 USD = ? ${c}`}>
            <input className={input} type="number" step="0.0001" value={rates[c]} disabled={c === 'USD'} onChange={e => setRates({ ...rates, [c]: e.target.value })} />
          </Field>
        ))}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button className={btnGhost} onClick={onClose}>Cancel</button>
        <button className={btn} disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save snapshot'}</button>
      </div>
    </Modal>
  )
}

// --------------------------------------------------------------- Import
const IMPORT_COLS = ['startup_name', 'invested_at', 'denomination_ccy', 'stage', 'vehicle', 'lead', 'cost_instrument_type', 'cost_shares', 'cost_share_price', 'cost_amount', 'cost_valuation_cap', 'cur_shares', 'cur_share_price', 'cur_valuation_cap']

export function ImportModal({ portfolioId, onClose, onSaved }:
  { portfolioId: string; onClose: () => void; onSaved: () => void }) {
  const [text, setText] = useState('')
  const [result, setResult] = useState<any>(null)
  const [busy, setBusy] = useState(false)
  const numCols = new Set(['cost_shares', 'cost_share_price', 'cost_amount', 'cost_valuation_cap', 'cur_shares', 'cur_share_price', 'cur_valuation_cap'])

  const parse = (): any[] => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean)
    if (!lines.length) return []
    const header = lines[0].split(',').map(h => h.trim())
    return lines.slice(1).map(line => {
      const cells = line.split(',')
      const row: any = {}
      header.forEach((h, i) => {
        let v: any = (cells[i] ?? '').trim()
        if (v === '') { v = null } else if (numCols.has(h)) { v = Number(v) }
        if (v !== null) row[h] = v
      })
      if (!row.denomination_ccy) row.denomination_ccy = 'USD'
      if (!row.cost_instrument_type) row.cost_instrument_type = 'common'
      return row
    })
  }

  const submit = async () => {
    setBusy(true); setResult(null)
    try {
      const rows = parse()
      const r = await api.post(`/private/portfolios/${portfolioId}/import`, rows)
      setResult(r.data)
      if (r.data.created) onSaved()
    } catch (e: any) { setResult({ error: e?.response?.data?.detail || 'Import failed' }) }
    finally { setBusy(false) }
  }

  return (
    <Modal title="Import investments (CSV)" onClose={onClose} wide>
      <p className="mb-2 text-xs text-gray-500">Paste CSV with a header row. Recognised columns:</p>
      <code className="mb-3 block overflow-x-auto rounded bg-gray-50 px-2 py-1 text-[11px] text-gray-600">{IMPORT_COLS.join(', ')}</code>
      <textarea className={`${input} h-48 font-mono text-xs`} value={text} onChange={e => setText(e.target.value)}
        placeholder={'startup_name,invested_at,denomination_ccy,stage,vehicle,lead,cost_instrument_type,cost_shares,cost_share_price,cur_shares,cur_share_price\nAcme,2021-03-01,USD,seed,direct,Foo Ventures,common,1000,20,1000,55'} />
      {result && (
        <div className={`mt-3 rounded px-3 py-2 text-sm ${result.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {result.error ? result.error : `Imported ${result.created} rows${result.errors?.length ? `, ${result.errors.length} errors` : ''}.`}
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button className={btnGhost} onClick={onClose}>Close</button>
        <button className={btn} disabled={busy || !text.trim()} onClick={submit}>{busy ? 'Importing…' : 'Import'}</button>
      </div>
    </Modal>
  )
}

// --------------------------------------------------------------- Valuation history
export function ValuationModal({ investment, onClose, onSaved }:
  { investment: any; onClose: () => void; onSaved: () => void }) {
  const [rows, setRows] = useState<any[]>([])
  const [f, setF] = useState<any>({ as_of: new Date().toISOString().slice(0, 10), residual_value: '', company_valuation: '', note: '' })
  const [busy, setBusy] = useState(false)
  const load = () => api.get(`/private/investments/${investment.id}/valuations`).then(r => setRows(r.data.valuations))
  useEffect(() => { load() }, [investment.id])
  const submit = async () => {
    setBusy(true)
    try {
      await api.post(`/private/investments/${investment.id}/valuations`, {
        as_of: f.as_of,
        residual_value: f.residual_value === '' ? null : Number(f.residual_value),
        company_valuation: f.company_valuation === '' ? null : Number(f.company_valuation),
        note: f.note || null,
      })
      await load(); onSaved()
      setF({ ...f, residual_value: '', company_valuation: '', note: '' })
    } finally { setBusy(false) }
  }
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value })
  return (
    <Modal title={`Valuation history — ${investment.startup_name}`} onClose={onClose}>
      <p className="mb-2 text-xs text-gray-500">Latest mark drives the current residual value ({investment.denomination_ccy}).</p>
      <div className="grid grid-cols-4 gap-2">
        <Field label="As of"><input type="date" className={input} value={f.as_of} onChange={set('as_of')} /></Field>
        <Field label="Residual"><input type="number" className={input} value={f.residual_value} onChange={set('residual_value')} /></Field>
        <Field label="Company val."><input type="number" className={input} value={f.company_valuation} onChange={set('company_valuation')} /></Field>
        <Field label="Note"><input className={input} value={f.note} onChange={set('note')} /></Field>
      </div>
      <div className="mt-2 flex justify-end"><button className={btn} disabled={busy} onClick={submit}>Add mark</button></div>
      <table className="mt-4 min-w-full text-sm">
        <thead><tr className="text-left text-xs text-gray-500"><th className="py-1">As of</th><th>Residual</th><th>Company val.</th><th>Note</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-t border-gray-100"><td className="py-1">{r.as_of}</td><td>{r.residual_value?.toLocaleString() ?? '—'}</td><td>{r.company_valuation?.toLocaleString() ?? '—'}</td><td className="text-gray-500">{r.note}</td></tr>
          ))}
          {!rows.length && <tr><td colSpan={4} className="py-4 text-center text-gray-400">No marks yet.</td></tr>}
        </tbody>
      </table>
      <div className="mt-4 flex justify-end"><button className={btnGhost} onClick={onClose}>Close</button></div>
    </Modal>
  )
}

// --------------------------------------------------------------- Documents
export function DocumentsModal({ investment, onClose }: { investment: any; onClose: () => void }) {
  const [rows, setRows] = useState<any[]>([])
  const [busy, setBusy] = useState(false)
  const load = () => api.get(`/private/investments/${investment.id}/documents`).then(r => setRows(r.data.documents))
  useEffect(() => { load() }, [investment.id])
  const upload = async (e: any) => {
    const file = e.target.files?.[0]; if (!file) return
    setBusy(true)
    try {
      const fd = new FormData(); fd.append('file', file)
      await api.post(`/private/investments/${investment.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      await load()
    } finally { setBusy(false); e.target.value = '' }
  }
  const download = async (d: any) => {
    const r = await api.get(`/private/documents/${d.id}`, { responseType: 'blob' })
    const url = URL.createObjectURL(r.data); const a = document.createElement('a')
    a.href = url; a.download = d.file_name; a.click(); URL.revokeObjectURL(url)
  }
  const del = async (id: string) => { if (confirm('Delete this document?')) { await api.delete(`/private/documents/${id}`); load() } }
  return (
    <Modal title={`Documents — ${investment.startup_name}`} onClose={onClose}>
      <label className="mb-3 inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50">
        <Upload size={15} />{busy ? 'Uploading…' : 'Upload file (SAFE, SPA, cap table…)'}
        <input type="file" className="hidden" onChange={upload} />
      </label>
      <table className="min-w-full text-sm">
        <thead><tr className="text-left text-xs text-gray-500"><th className="py-1">File</th><th>Type</th><th>Size</th><th></th></tr></thead>
        <tbody>
          {rows.map(d => (
            <tr key={d.id} className="border-t border-gray-100">
              <td className="py-1 font-medium">{d.file_name}</td>
              <td className="text-gray-500">{d.content_type || '—'}</td>
              <td className="text-gray-500">{d.size_bytes ? `${(d.size_bytes / 1024).toFixed(0)} kB` : '—'}</td>
              <td><div className="flex gap-1">
                <button className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600" onClick={() => download(d)}><Download size={14} /></button>
                <button className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600" onClick={() => del(d.id)}><Trash2 size={14} /></button>
              </div></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={4} className="py-4 text-center text-gray-400">No documents.</td></tr>}
        </tbody>
      </table>
      <div className="mt-4 flex justify-end"><button className={btnGhost} onClick={onClose}>Close</button></div>
    </Modal>
  )
}
